// © Microsoft Corporation. All rights reserved.

// Tracking information aggregate object.
// used by all the tracking systems to get common info sent via tracking

import dom from "./dom";
import extend from "./extend";
import { headData, getCookie, appEnvironment } from "@msnews/core";
import mediator from "./mediator";
import pageInstance from "./pageInstance";
import telemetryConstants from "./telemetryconstants";
import { telemetryDataTypes } from "./telemetryDataTypes";
import { telemetryReadyCallback } from "./telemetryReadyCallback";
import visibilityService from "./visibility";
import { AccountType, AadAccountType } from "@msnews/experiences-constants";
import { canUseDOM, getLocationHref } from "@msnews/isomorphic-util";

var clientClientId;
var browserWidth;
var browserHeight;
// eventIndex[0] is for the first rid and eventIndex[1] is for the current rid
var eventIndex = [0, 0];
var isFirstEventIndex = true;
var eventTransientCustomTags = {}; //eventTransientCustomTags is applied to each specific events.

var modules = [];
var baseUrl = "";
var clientIdTypeValue;

var userDefinedCustomTags = {};

function Module(element, visible) {
    this.element = element;
    this.visible = visible;

    if (this.visible) {
        this.compress = function () {
            var moduleTelemetryData = this.element.getAttribute("data-m");
            try {
                return JSON.parse(moduleTelemetryData);
            } catch (e) {
                return moduleTelemetryData;
            }
        };
    }
}

function getBrowserSize() {
    /// <summary>
    ///      Helper function used to get the browser height and width
    /// </summary>

    browserWidth = (canUseDOM()) ? (window.outerWidth || document.documentElement.clientWidth) : 0;
    browserHeight = (canUseDOM()) ? (window.outerHeight || document.documentElement.clientHeight) : 0;

}

function getWindowHeight() {
    /// <summary>
    ///     Get current browser viewpane height
    /// </summary>

    if (!canUseDOM()) {
        return 0;
    }

    return window.innerHeight ||
        document.documentElement.clientHeight ||
        document.body.clientHeight || 0;
}

function getWindowYscroll() {
    /// <summary>
    ///     Get current absolute window scroll position
    /// </summary>

    if (!canUseDOM()) {
        return 0;
    }

    return window.pageYOffset ||
        document.body.scrollTop ||
        document.documentElement.scrollTop || 0;
}

function getDocHeight() {
    /// <summary>
    ///     Get current absolute document height
    /// </summary>

    if (!canUseDOM()) {
        return 0;
    }

    return Math.max(
        document.body.scrollHeight || 0,
        document.documentElement.scrollHeight || 0,
        document.body.offsetHeight || 0,
        document.documentElement.offsetHeight || 0,
        document.body.clientHeight || 0,
        document.documentElement.clientHeight || 0
    );
}

function getDefaultCustomTags() {
    /// <summary>
    ///     Get the default custom tags.
    /// </summary>
    var defaultCustomTags = {};

    if (!canUseDOM()) {
        return defaultCustomTags;
    }

    // @ts-ignore
    if (window && window.adsEnabled != null) {
        // @ts-ignore
        defaultCustomTags.adoni = +window.adsEnabled;
    }

    return defaultCustomTags;
}

function joinTelemetryValues(separator, leftTmplValue, rightTemplValue) {
    /// <summary>Composes a new template value from an exisiting one and a new one.</summary>
    /// <param name="separator">The separator.</param>
    /// <param name="leftTmplValue">The left tmpl value string, may be null or empty.</param>
    /// <param name="rightTemplValue">The right tmpl value string, may be null or empty.</param>
    /// <returns type="string">
    /// A new string composed of the right and left values with a semi-colon separator if both values are non-null and not empty.
    /// If the right templ value is null or empty (or otherwise falsey), the left templ value is returned.
    /// If only the left templ value is falsey, only the right templ value is returned.
    /// </returns>

    return rightTemplValue
        ? leftTmplValue
            ? [leftTmplValue, rightTemplValue].join(separator)
            : rightTemplValue
        : leftTmplValue;
}

function joinTelemetryValuesForTmpl(separator, currentTmplValue, newTmplValue) {
    /// <summary>Composes a new template value from an exisiting one and a new one.</summary>
    /// <param name="separator">The separator.</param>
    /// <param name="currentTmplValue">The currentTmplValue, may be null or empty.</param>
    /// <param name="newTmplValue">The newTmplValue, may be null or empty.</param>
    /// <returns type="string">
    /// A new string composed of the newTmplValue and currentTmplValue with a semi-colon separator if both values are non-null and not empty.
    /// If the newTmplValue is null or empty (or otherwise falsey), the currentTmplValue is returned.
    /// If only the currentTmplValue is falsey, only the newTmplValue is returned.
    /// Duplicate key will override the value even if the value is empty string.
    /// Value without any key will be ignored.
    /// </returns>

    if (!newTmplValue) {
        return currentTmplValue ? currentTmplValue : "";
    }
    if (!currentTmplValue) {
        return newTmplValue ? newTmplValue : "";
    }
    var combinedTelemetryVal = {};
    var array = [];
    var isDuplicateKeyExist = false;
    var newTmplValueSplited = newTmplValue.split(separator);
    for (var i = 0; i < newTmplValueSplited.length; i++) {
        var newKeyVal = newTmplValueSplited[i].split(":");
        if (newKeyVal[0]) {
            if (!isDuplicateKeyExist) {
                // 'currentTmplValue' will be split out only if 'newTmplValue' contains any key which is exist in 'currentTmplValue'
                if (newKeyVal[0] && (currentTmplValue.indexOf(newKeyVal[0]) != -1)) {
                    var currentTmplValueSplited = currentTmplValue.split(separator);
                    for (var j = 0; j < currentTmplValueSplited.length; j++) {
                        if (currentTmplValueSplited[j]) {
                            var currentKeyVal = currentTmplValueSplited[j].split(":");
                            combinedTelemetryVal[currentKeyVal[0]] = currentKeyVal[1];
                        }
                    }
                    isDuplicateKeyExist = true;
                }
            }
            combinedTelemetryVal[newKeyVal[0]] = newKeyVal[1];
        }
    }

    var val = "";
    for (var key in combinedTelemetryVal) {
        if (key) {
            if (val !== "") {
                val += separator;
            }

            val += key;
            if (combinedTelemetryVal[key]) {
                val += ":" + combinedTelemetryVal[key];
            }
        }
    }

    if (!isDuplicateKeyExist && currentTmplValue) {
        val = val ? val + separator + currentTmplValue : currentTmplValue;
    }

    return val;
}

export var trackInfo = {
    notrack: "notrack",
    cmSeparator: ">",
    // TODO: (nubenja) To be removed. Make sure all dependecies get removed
    defaultModule: "body",
    defaultFormHeadline: "[form submit]",
    piitxt: "data-piitxt",
    piiurl: "piiurl",
    wrapperId: "wrapper",
    MUIDCookie: "MUID",            // This is used for client id. If MUID exists, then we will use MUID as client id otherwise use request id.
    gestureKey: "touch_gesture",
    gestureType:
    {
        tap: "tap",
        rightclick: "rightclick"
    },
    eventTypes:
    {
        click: "click",
        nonNavClckEvt: "click_nonnav",
        submit: "submit",
        contextmenu: "contextmenu",
        mouseenter: "mouseenter",
        imprUpdate: "impr_update",
        adPlay: "AdPlay",
        contentPlay: "ContentPlay",
        videoClickNonNav: "Click_Non-nav"
    },
    imprUpdateMechanisms:
    {
        load: "load",
        scroll: "scroll",
        auto: "auto",
        tap: "tap",
        resize: "resize",
        swipe: "swipe",
        partial: "partial",
        view: "view"
    },
    event: {},
    sitePage: {},
    userStatic: {},
    overrideClientParams: {},
    persistOverriddenParams: false,
    customTags: getDefaultCustomTags() || {}, //customTags is applied to all events.
    trackingDisabled: false, // used by NTP/DHP to disable tracking in "enterprise mode",
    shouldFireTelemetry: function () {
        /// <summary>
        ///     Return whether telemetry beacons should get fired or not.
        /// </summary>

        return !this.trackingDisabled;
    },
    appErrorEnabled: true, // should not send app error when user is navigating away from page.
    shouldFireAppError: function () {
        /// <summary>
        ///     Return whether app error beacons should get fired or not.
        /// </summary>

        return this.appErrorEnabled;
    },
    getCurrentTime: function () {
        /// <summary>
        ///    gets the current timestamp
        /// </summary>
        /// <returns type="datetime">
        /// </returns>
        return +new Date();
    },
    timeToInteract: function (eventTime?) {
        /// <summary>
        ///     gets the time to interact for the event by substracting it from navigationStart.
        ///     https://developer.mozilla.org/en-US/docs/Web/API/PerformanceTiming/navigationStart
        /// </summary>
        /// <returns type="time in milliseconds substracted from navigation start.">
        /// </returns>
        if (!canUseDOM()) {
            return 0;
        }

        var eventTiming;
        if (eventTime) {
            eventTiming = eventTime;
        } else {
            eventTiming = trackInfo.getCurrentTime();
        }
        if (eventTiming && window.performance && window.performance.timing && window.performance.timing.navigationStart) {
            return (eventTiming - window.performance.timing.navigationStart);
        }
    },
    // This will be deprecated in upcoming iterations, so please use telemetryDataTypes.TelemetryType. Please follow these new definitions: https://microsoft.sharepoint.com/:x:/t/IDXTelemetryandDatafabric/EbMw_1_6OCtNtyXkbb2YcjoBxY2iFtWOW-kfQZLJmnErEw
    telemetryModuleTypes:
    {
        Undefined: 0,
        Header: 1,
        Footer: 2,
        topNavBar: 3,
        navBar: 4,
        dropDown: 5,
        Section: 6,
        Carousel: 7,
        Module: 8,
        searchBox: 9,
        inputBox: 10,
        actionButton: 11,
        Interaction: 12,
        Headline: 13,
        Navigation: 14,
        Entity: 15,
        structuredData: 16,
        appStore: 17,
        appLaunch: 18,
        cmsPromotions: 19,
        microsoftWebStore: 20,
        xboxStore: 21,
        officeStore: 22,
        appInstall: 23,
        PartnerLinks: 24,
        SponsoredContent: 25
    },
    contentPlacedBySource:
    {
        Editorial: 0,
        PersonalizationAlgo: 1,
        BingAlgo: 2,
        Taboola: 3,
        Outbrain: 4
    },
    telemetryDataAttribute: "data-m",
    telemetryHLAttribute: "data-hl",
    client:
    {
        // client id
        clientId: function () {
            if (!clientClientId && clientClientId !== "") {
                // @ts-ignore
                clientClientId = getCookie(trackInfo.MUIDCookie) || trackInfo.sitePage.requestId || "";
            }

            return clientClientId;
        },
        clientIdType: function () {
            // @ts-ignore
            if (clientClientId === trackInfo.sitePage.requestId) {
                clientIdTypeValue = "activityId";
            } else if (clientClientId !== "") {
                clientIdTypeValue = "muid";
            }

            return clientIdTypeValue;
        },
        gesture: function () {
            var element = dom.getTarget(trackInfo.event);
            if (element && element[trackInfo.gestureKey]) {
                return element[trackInfo.gestureKey]
            }
            var trackInfoEvent: any = trackInfo.event;
            if (trackInfoEvent) {
                if ((trackInfoEvent.originalEvent && trackInfoEvent.originalEvent == 'contextmenu') || trackInfoEvent.ctrlKey || trackInfoEvent.shiftKey) {
                    return trackInfo.gestureType.rightclick
                }
            }
            return ''
        },

        getMuid: function () {
            return getCookie(trackInfo.MUIDCookie);
        },

        getAnonCookieName: function () {
            /// <summary>
            ///    Gets the anonCookieName from the headData (hybrid) or the appEnvironment. ANON for Pages with manual sign in, else APP_ANON.
            /// </summary>
            /// <returns type="string">
            /// </returns>
            return headData.AnonCookieName || appEnvironment.AuthCookieName;
        },

        getSsoComplete: function () {
            /// <summary>
            ///    Gets the SsoComplete state from the headData. true if sso completes otherwise false.
            ///    Used only for prime. DHP/NTP does not have anon.
            /// </summary>
            /// <returns type="bool">
            /// </returns>

            if (headData.SsoComplete && (headData.SsoComplete + '').toLowerCase() === 'true') {
                return 1;
            }

            return 0;
        },

        /**
         * Gets the Aad state from the headData or appEnvironment
         * @returns {number} The AAD authenticated state.
         */
        getAadAuthentication: function (): AadAccountType {
            // Check the head data for the hybrid page
            if (headData.AadState === true) {
                // AAD AuthenticatedState
                return AadAccountType.AadAccount;
            } else if (headData.AadState === false) {
                // AAD UnauthenticatedState
                return AadAccountType.NonAadAccount;
            }

            // Check the headData first for the Hybrid page, else use appEnvironment
            const accountType: AccountType = appEnvironment.AccountType;
            const isAadAccount: boolean = (accountType === AccountType.AAD)
            return isAadAccount ? AadAccountType.AadAccount : AadAccountType.NonAadAccount;
        },

        /**
         * Gets the device orientation
         */
        orientation: function () {
            if (!canUseDOM()) {
                return "";
            }
            return screen.width > screen.height ? "landscape" : "portrait";
        },

        OTFTelemetry: function (mechanism?: string): void {
            {
                if (!canUseDOM()) {
                    return;
                }

                if (typeof mechanism === "undefined") {
                    // mechanism is only undefined on page load.
                    // Peregrine already sends an impression update on page load through
                    // EntryPoint.connector#onVisuallyReady, so this one can be skipped.
                    return;
                }

                if (mechanism == null) {
                    mechanism = "";
                }

                modules = [];
                var telemetryData = document.body.querySelectorAll("[data-m]");

                // calculate windowRight, windowLeft, and windowHeight upfront to avoid multiple calls to getBoundingClientRect() and
                // reduce performance impact
                var windowHeight;
                var windowRight;

                windowHeight = window.innerHeight;
                windowRight = window.innerWidth;

                var windowLeft = 0;
                var contentArea = document.getElementById("content");

                if (contentArea) {
                    var contentRect = contentArea.getBoundingClientRect();
                    windowRight = contentRect.right;
                    windowLeft = contentRect.left;
                }

                // Everything inside infopane is considered visible as long as infopane itself is visible due to auto rotation
                // First get infopane itself ("mip" is for infopane in mid-level, "sip" is for spartan infopane).
                var infopane = document.body.querySelector(".ip") || document.body.querySelector(".mip") || document.body.querySelector(".sip");
                var infopaneVisible = infopane && trackInfo.telemetryTracking.isVisible(infopane, windowRight, windowLeft, windowHeight);

                // cache result of IsVisible() to reduce performance impact caused by getBoundingClientRect()
                var visibles = [];
                for (var i = 0; i < telemetryData.length; i++) {
                    // if otfUseViewportForVisibility is true, we will use the whole viewport to determine the visibility of the element
                    if (telemetryData[i].getAttribute("data-otfUseViewportForVisibility") === "true") {
                        // @ts-ignore window.width
                        visibles.push(trackInfo.telemetryTracking.isVisible(telemetryData[i], window.width, 0, windowHeight, infopaneVisible));
                    } else {
                        visibles.push(trackInfo.telemetryTracking.isVisible(telemetryData[i], windowRight, windowLeft, windowHeight, infopaneVisible));
                    }
                }

                for (var j = 0; j < telemetryData.length; j++) {
                    modules.push(new Module(telemetryData[j], visibles[j]));
                }
                var context = trackInfo.telemetryTracking.formatModules();
                // @ts-ignore
                if (context && ((context.e && context.e.length > 0) || (context.vc && context.vc.length > 0))) {
                    trackInfo.telemetryTracking.sendImprUpdateEvent(mechanism, context);
                }
            }
        },

        // browser height
        height: function () {
            if (!browserHeight) {
                getBrowserSize();
            }
            return browserHeight;
        },

        pageTitle: function () {
            /**
             * returns the page title
             */
            if (!canUseDOM()) {
                return "";
            }

            return document.title;
        },

        // entire url of the current page
        // use a function because we may virtually change the page by manipulating the history stack
        // (for example, in a gallery when changing slides).
        pageUrl: function () {
            var pageUrl = getLocationHref();
            // @ts-ignore
            var ocid = trackInfo.sitePage.msnocid;
            if (ocid) {
                // This is the DHP or NTP page - need to add the OCID to pageUrl.
                var hasQueryString = pageUrl.indexOf("?") >= 0;
                pageUrl = hasQueryString ? pageUrl + "&" : pageUrl + "?";
                pageUrl = pageUrl + "ocid=" + ocid;
            }

            return pageUrl;
        },

        // URL of the document that loaded the current document
        // first check for an override property that we may set during virtual page changes, and if
        // that's not set, use the real referrer.
        // @ts-ignore
        referrer: function () {
            if (!canUseDOM()) {
                return "";
            }

            // @ts-ignore
            return document.referrerOverride || document.referrer;
        },
        // screen resolution
        screenResolution: function () {
            if (!canUseDOM()) {
                return "";
            }

            var screenResolution = [screen.width, screen.height].join("x");

            return screenResolution;
        },

        userAgent: function () {
            /**
             * gets the user agent
             */
            if (!canUseDOM()) {
                return "";
            }

            return navigator.userAgent;
        },

        // browser width
        width: function () {
            if (!browserWidth) {
                getBrowserSize();
            }
            return browserWidth;
        },
        //sends the Browser Hidden Field on window
        wasBrowserHiddenAtLeastOnce: function () {
            return visibilityService.wasBrowserHiddenAtLeastOnce();
        }
    },
    userDynamic:
    {
        timeStamp: function () {
            /// <summary>
            ///    generates the timestamp for the url
            /// </summary>
            /// <returns type="string">
            /// </returns>
            return trackInfo.getCurrentTime();
        },
        slideType: function () {
            /// <summary>
            ///     Finds the slide type if any (and if a slide)
            /// </summary>
            /// <returns type="string">"halfpane" or null</returns>
            if (!canUseDOM()) {
                return null;
            }

            // @ts-ignore
            var elem = trackInfo.event.target;
            var halfpaneClass = "halfpane";
            while (elem && dom.name(elem) != "SECTION") {
                if (dom.name(elem) == "LI" && dom.containsClass(elem, halfpaneClass)) {
                    return halfpaneClass;
                }
                elem = dom.parent(elem);
            }
            return null;
        },
        firstEventIndex: function () {
            /// <summary>
            ///     The first rid event index.
            ///     This value gets incremented for each event of the first rid that is triggered
            /// </summary>
            /// <returns type="int">
            ///     Sequential number of the current event
            /// </returns>

            return ++eventIndex[0];
        },
        currentEventIndex: function () {
            /// <summary>
            ///     The current event index.
            ///     This value gets incremented for each event that is triggered
            /// </summary>
            /// <returns type="int">
            ///     Sequential number of the current event
            /// </returns>
            if (isFirstEventIndex) {
                return ++eventIndex[0];
            } else {
                return ++eventIndex[1];
            }
        },
        requestId: function () {
            /// <summary>
            ///     return the requestId
            /// </summary>
            return pageInstance.getRequestId() || (<any>trackInfo.sitePage).requestId;
        },
        resetEventIndex: function () {
            /// <summary>
            ///     reset the event index
            /// </summary>
            eventIndex[1] = 0;
            isFirstEventIndex = false;
        }
    },
    custom:
    {
        addCustomTags: function () {
            /// <summary>
            ///    Adds the custom tags inside the otf beacon url.
            /// </summary>
            /// <returns type="string">
            /// </returns>

            var combinedTags = trackInfo.customTags;
            combinedTags = extend({}, trackInfo.customTags, eventTransientCustomTags);
            //reset so the next events gets its own transient custom tags
            eventTransientCustomTags = {};
            return JSON.stringify(combinedTags);
        },
        addCustomTagsForClickEvent: function () {
            /// <summary>
            ///    Adds the custom tags for click event inside the otf beacon url.
            /// </summary>
            /// <returns type="string">
            /// </returns>

            var customTags = "";
            if (typeof userDefinedCustomTags === "object") {
                for (var key in userDefinedCustomTags) {
                    customTags = JSON.stringify(userDefinedCustomTags);
                    break;
                }
            }
            //reset so the next events gets its own custom tags
            userDefinedCustomTags = {};
            return customTags;
        }
    },
    telemetryTracking:
    {
        setupParameters: function (evt) {
            var trackInfoSitePage = trackInfo.sitePage;
            const anonckName: string = appEnvironment.AuthCookieName as string;

            var result = {
                evt: evt,
                // @ts-ignore
                rid: pageInstance.getRequestId() || trackInfoSitePage.requestId,
                // @ts-ignore
                di: trackInfoSitePage.domainId,
                clid: trackInfo.client.clientId(),
                issso: trackInfo.client.getSsoComplete(),
                aadState: trackInfo.client.getAadAuthentication()
            };

            // @ts-ignore
            if (trackInfoSitePage.afd) {
                // @ts-ignore
                result.afd = trackInfoSitePage.afd;
            }

            if (anonckName) {
                // @ts-ignore
                result.anoncknm = anonckName;
            }

            // Set the new page parameters for all post events
            // @ts-ignore
            result["pg.p"] = trackInfo.sitePage.page_product;
            // @ts-ignore
            result["pg.t"] = trackInfo.sitePage.page_type;
            // @ts-ignore
            result["pg.n"] = trackInfo.sitePage.page_name;
            // @ts-ignore
            result["pg.c"] = trackInfo.sitePage.page_configuration;

            return result;
        },
        sendImprUpdateEvent: function (mechanism, modules, topLevelContainerType?) {
            // Prevent empty impr_update
            if (!canUseDOM() || modules == undefined || ((modules.e == undefined || modules.e.length <= 0) && (modules.vc == undefined || modules.vc.length <= 0))) {
                return;
            }

            var parameters = this.setupParameters(trackInfo.eventTypes.imprUpdate);
            if (parameters) {
                // mechanism to trigger the viewability event. ex: load, swipe, click, tap, scroll, and resize
                parameters["mech"] = mechanism;

                parameters.winht = getWindowHeight();
                parameters.docht = getDocHeight();
                parameters.scrollOff = getWindowYscroll();

                parameters.wbh = visibilityService.wasBrowserHiddenAtLeastOnce();
                parameters["e1"] = modules;
                // @ts-ignore
                parameters.tmpl = trackInfo.sitePage.templ;
                // @ts-ignore
                parameters.mkt = trackInfo.sitePage.localeCode;
                if (mechanism === trackInfo.imprUpdateMechanisms.scroll) {
                    parameters.TTI = trackInfo.timeToInteract();
                }

                // @ts-ignore
                if (window && window.adsEnabled != null) {
                    // @ts-ignore
                    parameters.adoni = +window.adsEnabled;
                }

                // Feed parameters
                // @ts-ignore
                if (trackInfo.sitePage.feedId) {
                    // @ts-ignore
                    parameters.fid = trackInfo.sitePage.feedId;
                    // @ts-ignore
                    parameters.fn = trackInfo.sitePage.feedName;
                    // @ts-ignore
                    parameters.ft = trackInfo.sitePage.feedType;
                }

                // @ts-ignore
                if (trackInfo.sitePage.flightid) {
                    // @ts-ignore
                    parameters.flightid = trackInfo.sitePage.flightid;
                }

                // If toplevel container is passed to impression update then create a new parameter which will eventually be replaced by tlc.
                if (topLevelContainerType) {
                    parameters.tlcType = topLevelContainerType;
                }

                this.sendUpdate(parameters);
            }
        },
        isVisible: function (element, windowRight?, windowLeft?, windowHeight?, infopaneVisible?) {
            if (!element) {
                return false;
            }

            return visibilityService.isElementTrulyVisible(element, windowRight, windowLeft, windowHeight);
        },
        formatModules: function () {
            var formattedModules = [];

            for (var index in modules) {
                var module = modules[index];
                if (module && module.compress) {
                    var mData = module.compress();

                    // TODO: (nubenja) Remove this once data-m.y is deprecated
                    // Convertion from telemetryType (data-m.y) to telemetryContentType and telemetryContentAction (data-m.ct/ca)
                    if (mData && mData.y && !mData.ct) {
                        var ct = telemetryDataTypes.getTelemetryContentType(mData.y);

                        if (ct) {
                            mData.ct = ct;
                        }
                    }

                    formattedModules.push(mData);
                }

            }

            var context = {};
            if (formattedModules && formattedModules.length > 0) {
                // @ts-ignore
                context.e = formattedModules;
            }

            return context;
        },
        sendUpdate: function (parameters) {
            // For the preloaded pages, the code inside requestPageRevealAsync is called when page is revealed,
            // otherwise it is called right away.
            telemetryReadyCallback((): void => {
                if (!trackInfo.shouldFireTelemetry()) {
                    return;
                }

                // An ad call may send this without setting up the baseUrl correctly.
                // @ts-ignore
                baseUrl = baseUrl || (trackInfo.sitePage || {}).otfURL;
                if (!baseUrl) {
                    // The value of sitePage.otfURL was empty!
                    return;
                }

                if (!parameters) {
                    return;
                }

                // increment the event index and set the idx parameter
                parameters.idx = trackInfo.userDynamic.currentEventIndex();

                // An ad call may send this without setting up the baseUrl correctly.
                var beaconData = {
                    url: baseUrl,
                    requestType: telemetryConstants.POST,
                    // @ts-ignore
                    eventType: parameters.evt || trackInfo.eventTypes.impressionUpdate,
                    params: parameters,
                    trackingType: telemetryConstants.OTF
                };

                mediator.pub(telemetryConstants.FireTelemetry, beaconData);
            });
        }
    },
    updateTemplValue: function (templToAdd) {
        /// <summary>Updates the templ value with the specified value</summary>
        /// <param name="templToAdd" type="string">The templ value to add</param>

        trackInfo.updateSitePage(templToAdd);
    },
    updateSitePage: function (templ, flightId?, requestId?) {
        /// <summary>Updates the trackInfo.sitePage values with the specified values</summary>
        /// <param name="templ" type="string">The new templ value to add</param>
        /// <param name="newTrackInfo" type="string">The new flightId to add</param>
        /// <param name="newTrackInfo" type="string">The new trackInfo object</param>

        var currentSitePage = trackInfo.sitePage;
        if (!currentSitePage) {
            currentSitePage = trackInfo.sitePage = {};
        }

        // @ts-ignore
        var currentFlightIds = currentSitePage.flightid;
        if (flightId && (!currentFlightIds || (currentFlightIds.indexOf(flightId) < 0))) {
            // @ts-ignore
            currentSitePage.flightid = joinTelemetryValues(",", currentFlightIds, flightId);
        }
        // @ts-ignore
        currentSitePage.templ = joinTelemetryValuesForTmpl(";", currentSitePage.templ, templ);

        if (requestId) {
            // @ts-ignore
            currentSitePage.requestId = requestId;
        }
    },
    updatePageName: function (pageName, overridePageType) {
        /// <summary>Updates the pageName and pageType in the trackInfo.SitePage for the given pageName</summary>
        /// <param name="pageName" type="string">The pageName to update</param>
        /// <param name="overridePageType" type="string">Boolean flag to determine to override pagetype or not. Default is true</param>
        var doOverridePageType = overridePageType;
        if (overridePageType === undefined) {
            doOverridePageType = true;
        }

        var telemetryPageName = telemetryDataTypes.getTelemetryPageInfo(pageName);
        if (telemetryPageName && trackInfo.sitePage) {
            // @ts-ignore
            trackInfo.sitePage.page_name = telemetryPageName.PageName;
            if (doOverridePageType) {
                // @ts-ignore
                trackInfo.sitePage.page_type = telemetryPageName.PageType;
            }
        }
    },
    updateFeedMetadata: function (feedId, feedName, feedType) {
        /// <summary>Updates the feedid, feedname and feedType in the trackInfo.SitePage for the given feed</summary>
        /// <param name="feedId" type="string">The feedId to update</param>
        /// <param name="feedName" type="string">The feedName to update</param>
        /// <param name="feedType" type="string">The feedType to update</param>
        if (feedId) {
            // @ts-ignore
            trackInfo.sitePage.feedId = feedId;
            if (feedName) {
                // @ts-ignore
                trackInfo.sitePage.feedName = feedName;
            }

            if (feedType) {
                // @ts-ignore
                trackInfo.sitePage.feedType = feedType;
            }
        }
    },
    updatePagePivot: function (pagePivot) {
        /// <summary>Updates the pagePivot in the trackInfo.SitePage</summary>
        /// <param name="pagePivot" type="string">The pagePivot to update</param>
        if (pagePivot === telemetryDataTypes.TelemetryPagePivot.Undefined) {
            // @ts-ignore
            trackInfo.sitePage.pagePivot = null;
        } else if (telemetryDataTypes.validateTelemetryDataType(telemetryDataTypes.TelemetryPagePivot, pagePivot)) {
            // @ts-ignore
            trackInfo.sitePage.pagePivot = pagePivot;
        }
    },
    updateReferrer: function (referrer, referrerType) {
        /// <summary>Updates referrer and referrerType</summary>
        /// <param name="referrer" type="string">referrer</param>
        /// <param name="referrerType" type="string">TelemetryContentType of the referrer</param>
        if (canUseDOM() && referrer) {
            // @ts-ignore
            document.referrerOverride = referrer;

            if (referrerType && telemetryDataTypes.validateTelemetryDataType(telemetryDataTypes.TelemetryContentType, referrerType) &&
                referrerType != telemetryDataTypes.TelemetryContentType.Undefined) {
                // @ts-ignore
                trackInfo.sitePage.referrerType = referrerType;
            }
        }
    },
    setCustomTagsForClickEvent: function (key, value) {
        /// <summary>
        ///    Set user defined custom tags.
        /// </summary>
        /// <param name="key" type="string">The key</param>
        /// <param name="value" type="string">The value</param>
        if (key && value && value != undefined) {
            userDefinedCustomTags[key] = value;
        }
    },
    updatePageUserTypeAndUserId: function (userType, userId) {
        /// <summary>
        ///    Set user id and user id type.
        /// </summary>
        /// <param name="userType" type="string">The user id type</param>
        /// <param name="userId" type="string">The user id</param>

        // @ts-ignore
        trackInfo.sitePage.pageUserId = null;
        // @ts-ignore
        trackInfo.sitePage.pageUserType = null;

        if (telemetryDataTypes.validateTelemetryDataType(telemetryDataTypes.TelemetryUserType, userType)) {
            // @ts-ignore
            trackInfo.sitePage.pageUserType = userType;

            if (userId) {
                // @ts-ignore
                trackInfo.sitePage.pageUserId = userId;
            }
        }
    },
    trackCustomProperty: function (key, value) {
        /// <summary>Adds or updates the transient per event custom tags properties</summary>
        /// <param name="key" type="string">key to add</param>
        /// <param name="value" type="string">value to add</param>
        if (key && value != undefined) {
            key = key.toLowerCase();
            eventTransientCustomTags[key] = value;
        }
    },
    updateExtendedFlightId: function (namespace, flightIds) {
        /// <summary>Adds or updates extended flightId</summary>
        /// <param name="namespace" type="string">Partner namespace enum</param>
        /// <param name="flightIds" type="string">flightIds to add</param>

        var currentSitePage = trackInfo.sitePage;

        if (currentSitePage && flightIds && namespace &&
            telemetryDataTypes.validateTelemetryDataType(telemetryDataTypes.TelemetryPartnerNamespace, namespace)) {
            // @ts-ignore
            currentSitePage.extflightid = joinTelemetryValuesForTmpl("|", currentSitePage.extflightid, namespace + ":" + flightIds);
        }
    }
};
